import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// 20 minutes of inactivity → auto-logout.
const IDLE_MS = 20 * 60 * 1000;
// Warn the user 60 seconds before the logout.
const WARN_MS = IDLE_MS - 60 * 1000;
// Activity events reset the idle timer.
const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
const LS_KEY = "tiger:last_activity_ts";

const readTs = () => {
  try { return Number(localStorage.getItem(LS_KEY)) || 0; } catch { return 0; }
};
const writeTs = (v) => {
  try { localStorage.setItem(LS_KEY, String(v)); } catch (_) {}
};

export default function IdleTimer() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const warnToastId = useRef(null);

  const clearTimers = useCallback(() => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (warnToastId.current != null) {
      toast.dismiss(warnToastId.current);
      warnToastId.current = null;
    }
  }, []);

  const triggerLogout = useCallback(async (reason) => {
    clearTimers();
    writeTs(0);
    try { await logout(); } catch (e) { console.warn("[idle] logout failed", e); }
    toast.info(reason || "Sessione terminata per inattività (20 min). Accedi di nuovo.");
    navigate("/login", { replace: true });
  }, [clearTimers, logout, navigate]);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    writeTs(Date.now());
    warnTimer.current = setTimeout(() => {
      warnToastId.current = toast.warning(
        "Sessione in scadenza per inattività. Logout automatico tra 60 secondi.",
        { duration: 60000, id: "idle-warning" }
      );
    }, WARN_MS);
    logoutTimer.current = setTimeout(() => triggerLogout(), IDLE_MS);
  }, [clearTimers, triggerLogout]);

  // On mount with an authenticated user, if last recorded activity is older
  // than the idle window, force a fresh re-authentication.
  useEffect(() => {
    if (!user) {
      clearTimers();
      return undefined;
    }
    const last = readTs();
    if (last && Date.now() - last > IDLE_MS) {
      triggerLogout("Sessione scaduta. Accedi di nuovo per riprendere.");
      return undefined;
    }
    scheduleTimers();
    const onActivity = () => scheduleTimers();
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const prev = readTs();
      if (prev && Date.now() - prev > IDLE_MS) {
        triggerLogout("Sessione scaduta. Accedi di nuovo per riprendere.");
      } else {
        scheduleTimers();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimers();
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, scheduleTimers, clearTimers, triggerLogout]);

  return null;
}
